/**
 * TanStack Query hooks for the admin sellers surface (STORY-008-007).
 *
 * `useSellers`     — `useInfiniteQuery` over the cursor-paginated admin list.
 * `useSeller`      — `useQuery` for the drawer detail view.
 * `useSuspendSeller` / `useRestoreSeller` / `usePatchSeller` / `useNotifySeller`
 *   — `useMutation` wrappers that invalidate the list + detail caches on success.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createSeller,
  getSeller,
  listSellers,
  notifySeller,
  patchSeller,
  restoreSeller,
  sellerKeys,
  suspendSeller,
  type AdminNotificationBody,
  type AdminNotificationResponse,
  type AdminSeller,
  type AdminSellerCreate,
  type AdminSellerListResponse,
  type AdminSellerUpdate,
} from "@/features/admin/sellers/api";

const PAGE_LIMIT = 25;

export interface SellerFilters {
  /** Server-side `verification_status` filter; null = all. */
  verificationStatus: string | null;
}

export const DEFAULT_SELLER_FILTERS: SellerFilters = {
  verificationStatus: null,
};

export function useSellers(
  filters: SellerFilters = DEFAULT_SELLER_FILTERS,
): UseInfiniteQueryResult<InfiniteData<AdminSellerListResponse>, Error> {
  return useInfiniteQuery<
    AdminSellerListResponse,
    Error,
    InfiniteData<AdminSellerListResponse>,
    ReturnType<typeof sellerKeys.list>,
    string | null
  >({
    queryKey: sellerKeys.list({ verificationStatus: filters.verificationStatus }),
    queryFn: ({ pageParam }) =>
      listSellers({
        verification_status: filters.verificationStatus,
        cursor: pageParam,
        limit: PAGE_LIMIT,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

export function useSeller(
  id: string | null,
): UseQueryResult<AdminSeller, Error> {
  return useQuery<AdminSeller, Error>({
    queryKey: sellerKeys.detail(id ?? ""),
    queryFn: () => getSeller(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}

/** Invalidate the seller list + a specific detail entry after a mutation. */
function useInvalidateSeller(): (id: string) => Promise<void> {
  const qc = useQueryClient();
  return async (id: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "sellers"] }),
      qc.invalidateQueries({ queryKey: sellerKeys.detail(id) }),
    ]);
  };
}

export function useSuspendSeller(): UseMutationResult<
  AdminSeller,
  Error,
  { id: string; reason: string }
> {
  const invalidate = useInvalidateSeller();
  return useMutation<AdminSeller, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => suspendSeller(id, reason),
    onSuccess: (_data, { id }) => invalidate(id),
  });
}

export function useRestoreSeller(): UseMutationResult<AdminSeller, Error, string> {
  const invalidate = useInvalidateSeller();
  return useMutation<AdminSeller, Error, string>({
    mutationFn: (id) => restoreSeller(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

export function useCreateSeller(): UseMutationResult<
  AdminSeller,
  Error,
  { body: AdminSellerCreate; force?: boolean }
> {
  const qc = useQueryClient();
  return useMutation<AdminSeller, Error, { body: AdminSellerCreate; force?: boolean }>({
    mutationFn: ({ body, force }) => createSeller(body, { force }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "sellers"] }),
  });
}

export function usePatchSeller(): UseMutationResult<
  AdminSeller,
  Error,
  { id: string; body: AdminSellerUpdate }
> {
  const invalidate = useInvalidateSeller();
  return useMutation<AdminSeller, Error, { id: string; body: AdminSellerUpdate }>({
    mutationFn: ({ id, body }) => patchSeller(id, body),
    onSuccess: (_data, { id }) => invalidate(id),
  });
}

export function useNotifySeller(): UseMutationResult<
  AdminNotificationResponse,
  Error,
  { id: string; body: AdminNotificationBody }
> {
  return useMutation<
    AdminNotificationResponse,
    Error,
    { id: string; body: AdminNotificationBody }
  >({
    mutationFn: ({ id, body }) => notifySeller(id, body),
  });
}
